// src/pages/GroupDetailPage.tsx
// ✅ v2 — Ranking unificado: Flashcards + Questões via studyScore

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { StudyGroupDetailed, GroupRankingData } from '../components/dashboard/GroupTypes';
import Toast from '../components/Toast';
import GroupSettingsModal from '../components/dashboard/groups/GroupSettingsModal';
import { ArrowLeft, Users, Settings, Link, Flame, BookOpen, HelpCircle, Info } from 'lucide-react';
import './GroupDetailPage.css';
import InviteModal from '../components/dashboard/groups/InviteModal';

const API_BASE_URL = '/api';

const GroupDetailPage: React.FC = () => {
    const { groupId } = useParams<{ groupId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [loading, setLoading] = useState(true);
    const [group, setGroup] = useState<StudyGroupDetailed | null>(null);
    const [ranking, setRanking] = useState<GroupRankingData | null>(null);
    const [globalRank, setGlobalRank] = useState<number | null>(null);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [activeMetric, setActiveMetric] = useState<'score' | 'cards' | 'questions'>('score');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

    const loadGroupDetails = useCallback(async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/groups/my-groups`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                const groups = await response.json();
                const currentGroup = groups.find((g: StudyGroupDetailed) => g._id === groupId);
                if (currentGroup) {
                    setGroup(currentGroup);
                } else {
                    showToast('Grupo não encontrado', 'error');
                    navigate('/dashboard');
                }
            }
        } catch (error) {
            showToast('Erro ao carregar dados do grupo', 'error');
        }
    }, [groupId, navigate]);

    const loadGroupRanking = useCallback(async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/groups/${groupId}/ranking`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) setRanking(await response.json());
        } catch (error) {
            console.error('Erro ao carregar ranking:', error);
        }
    }, [groupId]);

    const loadGlobalRank = useCallback(async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/groups/global/ranking`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                const rank = data.ranking.findIndex((g: any) => g.groupId === groupId);
                setGlobalRank(rank !== -1 ? rank + 1 : null);
            }
        } catch (error) {
            console.error('Erro ao carregar posição global:', error);
        }
    }, [groupId]);

    const loadGroupData = useCallback(async () => {
        setLoading(true);
        await Promise.all([loadGroupDetails(), loadGroupRanking(), loadGlobalRank()]);
        setLoading(false);
    }, [loadGroupDetails, loadGroupRanking, loadGlobalRank]);

    useEffect(() => {
        if (groupId) loadGroupData();
    }, [groupId, loadGroupData]);

    const isGroupAdmin = (): boolean => {
        if (!group || !user) return false;
        return group.members.some(m => {
            if (!m.userId) return false;
            const memberUserId = typeof m.userId === 'string'
                ? m.userId
                : (m.userId as any)._id || m.userId;
            return memberUserId === user.userId && m.role === 'admin';
        });
    };

    const handleRegenerateCode = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE_URL}/groups/${groupId}/regenerate-code`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                await loadGroupDetails();
                showToast('Novo código gerado', 'success');
            }
        } catch {
            showToast('Erro ao gerar novo código', 'error');
        }
    };

    const showToast = (message: string, type: 'success' | 'error' | 'info') => {
        setToast({ message, type });
    };

    const formatDate = (date: Date | string): string => {
        return new Date(date).toLocaleDateString('pt-BR', {
            day: '2-digit', month: 'short', year: 'numeric'
        });
    };

    const getRankIcon = (index: number) => `${index + 1}º`;

    const getSortedRanking = () => {
        if (!ranking) return [];
        const list = [...ranking.ranking];
        switch (activeMetric) {
            case 'cards':
                return list.sort((a, b) => b.stats.cardsMastered - a.stats.cardsMastered);
            case 'questions':
                return list.sort((a, b) => b.stats.questionsCorrect - a.stats.questionsCorrect);
            default:
                return list.sort((a, b) => b.stats.studyScore - a.stats.studyScore);
        }
    };

    if (loading) {
        return (
            <div className="group-detail-page">
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Carregando grupo</p>
                </div>
            </div>
        );
    }

    if (!group) {
        return (
            <div className="group-detail-page">
                <div className="error-container">
                    <h2>Grupo não encontrado</h2>
                    <button onClick={() => navigate('/dashboard')}>Voltar</button>
                </div>
            </div>
        );
    }

    const sortedRanking = getSortedRanking();

    return (
        <div className="group-detail-page">
            <header className="group-header-new">
                <div className="header-left">
                    <button className="btn-back-minimal" onClick={() => navigate('/dashboard')}>
                        <ArrowLeft size={20} />
                    </button>
                    <h1 className="group-name-header">{group.name}</h1>
                </div>
                <div className="header-right">
                    <button
                        className="btn-settings-icon"
                        onClick={() => setShowInviteModal(true)}
                        title="Convidar membros"
                    >
                        <Link size={20} />
                    </button>
                    {group.groupIcon && (
                        <img src={group.groupIcon} alt={group.name} className="group-icon-header" />
                    )}
                </div>
            </header>

            <main className="group-content-new">
                <div className="ranking-header-new">
                    <h2>Ranking de Membros</h2>

                    <div className="ranking-actions">
                        <div className="ranking-stats">
                            <div className="stat-pill">
                                <Users size={16} />
                                <span>{group.memberCount} {group.memberCount === 1 ? 'membro' : 'membros'}</span>
                            </div>
                            {globalRank && (
                                <div className="stat-pill highlight">
                                    <span>#{globalRank} no ranking</span>
                                </div>
                            )}
                        </div>

                        <button
                            className="btn-settings-icon"
                            onClick={() => setShowSettingsModal(true)}
                            title="Configurações"
                        >
                            <Settings size={20} />
                        </button>
                    </div>
                </div>

                {/* Seletor de métrica + ícone de informação */}
                <div className="metric-selector-row">
                    <div className="metric-selector">
                        <button
                            className={`metric-btn ${activeMetric === 'score' ? 'active' : ''}`}
                            onClick={() => setActiveMetric('score')}
                        >
                            Pontuação Geral
                        </button>
                        <button
                            className={`metric-btn ${activeMetric === 'cards' ? 'active' : ''}`}
                            onClick={() => setActiveMetric('cards')}
                        >
                            <BookOpen size={14} />
                            Flashcards
                        </button>
                        <button
                            className={`metric-btn ${activeMetric === 'questions' ? 'active' : ''}`}
                            onClick={() => setActiveMetric('questions')}
                        >
                            <HelpCircle size={14} />
                            Questões
                        </button>
                    </div>

                    <div className="score-info-wrapper">
                        <Info size={16} className="score-info-icon" />
                        <div className="score-info-tooltip">
                            <p className="tooltip-title">Como a pontuação é calculada</p>
                            <p>Leva em conta os flashcards feitos e dominados, as questões respondidas, os acertos e o nível de dificuldade de cada questão.</p>
                        </div>
                    </div>
                </div>

                {ranking && (
                    <div className="ranking-table">
                        <div className="ranking-table-header">
                            <span className="col-rank">Pos.</span>
                            <span className="col-name">Membro</span>
                            <span className="col-stat col-primary">
                                {activeMetric === 'score' && 'Pontuação'}
                                {activeMetric === 'cards' && 'Dominados'}
                                {activeMetric === 'questions' && 'Acertos'}
                            </span>
                            <span className="col-stat col-secondary">
                                {activeMetric === 'score' && 'Cards / Questões'}
                                {activeMetric === 'cards' && 'Respondidos'}
                                {activeMetric === 'questions' && 'Respondidas'}
                            </span>
                            <span className="col-stat">Streak</span>
                            <span className="col-stat">Entrou em</span>
                        </div>

                        {sortedRanking.map((member, index) => (
                            <div
                                key={member.userId}
                                className={`ranking-table-row ${member.role === 'admin' ? 'is-admin' : ''} ${index < 3 ? `top-${index + 1}` : ''}`}
                            >
                                <span className="col-rank rank-badge">{getRankIcon(index)}</span>

                                <div className="col-name" data-rank={`${index + 1}º`}>
                                    {member.profilePhoto ? (
                                        <img
                                            src={member.profilePhoto || ''}
                                            alt={member.username}
                                            className="member-avatar"
                                        />
                                    ) : (
                                        <div className="member-avatar-placeholder">
                                            {member.username.charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div>
                                        <span className="member-username">{member.username}</span>
                                        {member.role === 'admin' && (
                                            <span className="admin-badge">Admin</span>
                                        )}
                                    </div>
                                </div>

                                {/* Stats desktop */}
                                <span className="col-stat col-primary">
                                    <strong>
                                        {activeMetric === 'score' && member.stats.studyScore}
                                        {activeMetric === 'cards' && member.stats.cardsMastered}
                                        {activeMetric === 'questions' && member.stats.questionsCorrect}
                                    </strong>
                                    {activeMetric === 'score' && <span className="pts-label"> pts</span>}
                                </span>

                                <span className="col-stat col-secondary secondary-info">
                                    {activeMetric === 'score' && (
                                        <>
                                            <span title="Flashcards dominados">
                                                <BookOpen size={12} /> {member.stats.cardsMastered}
                                            </span>
                                            <span className="dot-sep">·</span>
                                            <span title="Questões corretas">
                                                <HelpCircle size={12} /> {member.stats.questionsCorrect}
                                            </span>
                                        </>
                                    )}
                                    {activeMetric === 'cards' && member.stats.cardsAnswered}
                                    {activeMetric === 'questions' && member.stats.questionsAnswered}
                                </span>

                                <span className="col-stat">
                                    {member.stats.currentStreak > 0 ? (
                                        <span className="streak-badge">
                                            <Flame size={14} />
                                            {member.stats.currentStreak}
                                        </span>
                                    ) : '-'}
                                </span>

                                <span className="col-stat">{formatDate(member.joinedAt)}</span>

                                {/* Stats mobile — visível só em telas pequenas */}
                                <div className="mobile-stats-row">
                                    <div className="mobile-stat-item">
                                        <strong>
                                            {activeMetric === 'score' && `${member.stats.studyScore} pts`}
                                            {activeMetric === 'cards' && member.stats.cardsMastered}
                                            {activeMetric === 'questions' && member.stats.questionsCorrect}
                                        </strong>
                                        {activeMetric === 'cards' && <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>dominados</span>}
                                        {activeMetric === 'questions' && <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>acertos</span>}
                                    </div>

                                    {activeMetric === 'score' && (
                                        <>
                                            <span className="mobile-stat-divider">·</span>
                                            <div className="mobile-stat-item">
                                                <BookOpen size={13} />
                                                <span>{member.stats.cardsMastered}</span>
                                            </div>
                                            <span className="mobile-stat-divider">·</span>
                                            <div className="mobile-stat-item">
                                                <HelpCircle size={13} />
                                                <span>{member.stats.questionsCorrect}</span>
                                            </div>
                                        </>
                                    )}

                                    {activeMetric !== 'score' && (
                                        <>
                                            <span className="mobile-stat-divider">·</span>
                                            <div className="mobile-stat-item">
                                                <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                                    {activeMetric === 'cards' ? 'resp.' : 'respondidas'}
                                                </span>
                                                <span>{activeMetric === 'cards' ? member.stats.cardsAnswered : member.stats.questionsAnswered}</span>
                                            </div>
                                        </>
                                    )}

                                    {member.stats.currentStreak > 0 && (
                                        <>
                                            <span className="mobile-stat-divider">·</span>
                                            <div className="mobile-stat-item">
                                                <Flame size={13} style={{ color: '#D97706' }} />
                                                <span style={{ color: '#D97706', fontWeight: 600 }}>{member.stats.currentStreak}</span>
                                            </div>
                                        </>
                                    )}

                                    <span className="mobile-stat-divider">·</span>
                                    <div className="mobile-stat-item" style={{ fontSize: '11px' }}>
                                        {formatDate(member.joinedAt)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

            </main>

            {showInviteModal && group && (
                <InviteModal
                    isOpen={showInviteModal}
                    groupName={group.name}
                    inviteCode={group.inviteCode}
                    isAdmin={isGroupAdmin()}
                    onClose={() => setShowInviteModal(false)}
                    onRegenerateCode={handleRegenerateCode}
                />
            )}

            {showSettingsModal && (
                <GroupSettingsModal
                    isOpen={showSettingsModal}
                    group={group}
                    isAdmin={isGroupAdmin()}
                    onClose={() => setShowSettingsModal(false)}
                    onUpdate={loadGroupData}
                    onShowToast={showToast}
                />
            )}

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default GroupDetailPage;